import * as React from 'react';
import { connect } from 'react-redux';
import { Api } from '../../../typings/api';
import { StoreState } from '../../../reducers';
import VideoPlayer from '../../../components/VideoPlayer';
import AudioPlayer from '../../../components/AudioPlayer';
import { GalleryInfosState } from '../../Gallery/reducer';
import { StyledTheaterContent, Image, SvgIcon } from './styles';
import Utils from '../../../utils';
import UrlUtils from '../../../utils/url';
import DriveItemUtils from '../../../utils/driveItem';
import UserAgentUtils from '../../../utils/userAgent';
import { DownloadManagerState } from '../../DownloadManager/reducer';
import DownloadManagerUtils from '../../../utils/downloadManager';
import { OfflineManagerState } from '../../OfflineManager/reducer';

interface TheaterContentProps {
	driveItem?: Api.DriveItem;
	resolutions: { [k: string]: Api.ResolutionGroup };
	galleryInfos: GalleryInfosState;
	downloadManager: DownloadManagerState;
	offlineManager: OfflineManagerState;
	isZoomEnabled: boolean;
}

const TheaterContent = (props: TheaterContentProps) => {
	const {
		driveItem,
		resolutions,
		galleryInfos,
		downloadManager,
		offlineManager: { thumbMap },
		isZoomEnabled,
	} = props;
	const galleryConfig = galleryInfos.config;
	const isOnline = UserAgentUtils.isOnline();

	let isDownloaded = false;
	if (driveItem !== undefined) {
		isDownloaded = DownloadManagerUtils.isItemDownloaded(downloadManager, driveItem.id);
	}

	let element;
	const containerRef = React.useRef<HTMLDivElement>(null);
	const imageRef = React.useRef<HTMLImageElement>(null);
	const [isDragging, setIsDragging] = React.useState(false);
	const [pinchScale, setPinchScale] = React.useState(1);
	const [zoomImageStyle, setZoomImageStyle] = React.useState<React.CSSProperties | undefined>(undefined);
	const pinchScaleRef = React.useRef(1);
	const pinchScrollRafRef = React.useRef<number | null>(null);
	const dragStateRef = React.useRef({
		isDragging: false,
		startX: 0,
		startY: 0,
		startScrollLeft: 0,
		startScrollTop: 0,
	});
	const pinchStateRef = React.useRef({
		isPinching: false,
		startDistance: 0,
		startScale: 1,
	});
	const touchPanStateRef = React.useRef({
		isPanning: false,
		startX: 0,
		startY: 0,
		startScrollLeft: 0,
		startScrollTop: 0,
	});

	const getZoomMetrics = (scale: number) => {
		const container = containerRef.current;
		const image = imageRef.current;
		if (!container || !image || !image.naturalWidth || !image.naturalHeight) {
			return null;
		}

		const isVeryLarge = image.naturalWidth >= container.clientWidth * 2
			|| image.naturalHeight >= container.clientHeight * 2;
		const shouldUpscale = !isVeryLarge;
		const baseWidth = shouldUpscale ? image.naturalWidth * 2 : image.naturalWidth;
		const baseHeight = shouldUpscale ? image.naturalHeight * 2 : image.naturalHeight;
		const renderWidth = baseWidth * scale;
		const renderHeight = baseHeight * scale;
		const marginLeft = Math.max((container.clientWidth - renderWidth) / 2, 0);
		const marginTop = Math.max((container.clientHeight - renderHeight) / 2, 0);

		return { renderWidth, renderHeight, marginLeft, marginTop };
	};

	const stopDragging = () => {
		if (dragStateRef.current.isDragging) {
			dragStateRef.current.isDragging = false;
			setIsDragging(false);
		}
	};

	const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
		if (!isZoomEnabled || !driveItem || driveItem.type !== Api.DriveItemType.Image) {
			return;
		}
		const container = containerRef.current;
		if (!container) {
			return;
		}

		event.preventDefault();
		dragStateRef.current = {
			isDragging: true,
			startX: event.clientX,
			startY: event.clientY,
			startScrollLeft: container.scrollLeft,
			startScrollTop: container.scrollTop,
		};
		setIsDragging(true);
	};

	const getTouchDistance = (touches: React.TouchList): number => {
		if (touches.length < 2) {
			return 0;
		}
		const deltaX = touches[0].clientX - touches[1].clientX;
		const deltaY = touches[0].clientY - touches[1].clientY;
		return Math.sqrt((deltaX * deltaX) + (deltaY * deltaY));
	};

	const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
		if (!isZoomEnabled || !driveItem || driveItem.type !== Api.DriveItemType.Image) {
			return;
		}
		const container = containerRef.current;
		if (!container) {
			return;
		}
		if (event.touches.length === 2) {
			const distance = getTouchDistance(event.touches);
			pinchStateRef.current = {
				isPinching: true,
				startDistance: distance,
				startScale: pinchScale,
			};
			touchPanStateRef.current.isPanning = false;
		} else if (event.touches.length === 1) {
			touchPanStateRef.current = {
				isPanning: true,
				startX: event.touches[0].clientX,
				startY: event.touches[0].clientY,
				startScrollLeft: container.scrollLeft,
				startScrollTop: container.scrollTop,
			};
		}
	};

	const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
		if (!isZoomEnabled || !driveItem || driveItem.type !== Api.DriveItemType.Image) {
			return;
		}

		const pinchState = pinchStateRef.current;
		if (pinchState.isPinching && event.touches.length === 2) {
			event.preventDefault();
			const container = containerRef.current;
			if (!container) {
				return;
			}

			const previousScale = pinchScaleRef.current;
			const previousMetrics = getZoomMetrics(previousScale);
			if (!previousMetrics) {
				return;
			}

			const bounds = container.getBoundingClientRect();
			const midTouchX = (event.touches[0].clientX + event.touches[1].clientX) / 2;
			const midTouchY = (event.touches[0].clientY + event.touches[1].clientY) / 2;
			const focalX = midTouchX - bounds.left;
			const focalY = midTouchY - bounds.top;
			const contentX = container.scrollLeft + focalX - previousMetrics.marginLeft;
			const contentY = container.scrollTop + focalY - previousMetrics.marginTop;

			const currentDistance = getTouchDistance(event.touches);
			if (pinchState.startDistance > 0) {
				const nextScale = pinchState.startScale * (currentDistance / pinchState.startDistance);
				const baseMetrics = getZoomMetrics(1);
				if (!baseMetrics) {
					return;
				}
				const fitScale = Math.min(
					container.clientWidth / baseMetrics.renderWidth,
					container.clientHeight / baseMetrics.renderHeight
				);
				const minScale = Math.min(Math.max(fitScale, 0.1), 1);
				const clampedScale = Math.min(Math.max(nextScale, minScale), 4);
				const smoothedScale = previousScale + ((clampedScale - previousScale) * 0.35);
				if (Math.abs(smoothedScale - previousScale) < 0.002) {
					return;
				}
				pinchScaleRef.current = smoothedScale;
				setPinchScale(smoothedScale);

				if (pinchScrollRafRef.current !== null) {
					window.cancelAnimationFrame(pinchScrollRafRef.current);
				}

				pinchScrollRafRef.current = window.requestAnimationFrame(() => {
					const nextMetrics = getZoomMetrics(smoothedScale);
					if (!nextMetrics) {
						return;
					}
					const scaleRatio = nextMetrics.renderWidth / previousMetrics.renderWidth;
					const nextContentX = contentX * scaleRatio;
					const nextContentY = contentY * scaleRatio;
					const nextScrollLeft = nextContentX + nextMetrics.marginLeft - focalX;
					const nextScrollTop = nextContentY + nextMetrics.marginTop - focalY;
					const maxScrollLeft = Math.max(container.scrollWidth - container.clientWidth, 0);
					const maxScrollTop = Math.max(container.scrollHeight - container.clientHeight, 0);

					container.scrollLeft = Math.min(Math.max(nextScrollLeft, 0), maxScrollLeft);
					container.scrollTop = Math.min(Math.max(nextScrollTop, 0), maxScrollTop);
					pinchScrollRafRef.current = null;
				});
			}
			return;
		}

		if (event.touches.length === 1 && touchPanStateRef.current.isPanning) {
			event.preventDefault();
			const container = containerRef.current;
			if (!container) {
				return;
			}
			const diffX = event.touches[0].clientX - touchPanStateRef.current.startX;
			const diffY = event.touches[0].clientY - touchPanStateRef.current.startY;
			if (Math.abs(diffX) < 1 && Math.abs(diffY) < 1) {
				return;
			}
			const panDamping = 0.9;
			container.scrollLeft = touchPanStateRef.current.startScrollLeft - (diffX * panDamping);
			container.scrollTop = touchPanStateRef.current.startScrollTop - (diffY * panDamping);
		}
	};

	const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
		if (event.touches.length < 2) {
			pinchStateRef.current.isPinching = false;
			if (event.touches.length === 1) {
				const container = containerRef.current;
				if (container) {
					touchPanStateRef.current = {
						isPanning: true,
						startX: event.touches[0].clientX,
						startY: event.touches[0].clientY,
						startScrollLeft: container.scrollLeft,
						startScrollTop: container.scrollTop,
					};
				}
			} else {
				touchPanStateRef.current.isPanning = false;
			}
			return;
		}
		if (event.touches.length === 2) {
			const distance = getTouchDistance(event.touches);
			pinchStateRef.current = {
				isPinching: true,
				startDistance: distance,
				startScale: pinchScale,
			};
			touchPanStateRef.current.isPanning = false;
		}
	};

	const updateZoomImageStyle = () => {
		if (!isZoomEnabled || !driveItem || driveItem.type !== Api.DriveItemType.Image) {
			setZoomImageStyle(undefined);
			return;
		}

		const metrics = getZoomMetrics(pinchScale);
		if (!metrics) {
			setZoomImageStyle(undefined);
			return;
		}

		setZoomImageStyle({
			width: `${metrics.renderWidth}px`,
			height: `${metrics.renderHeight}px`,
			marginLeft: `${metrics.marginLeft}px`,
			marginTop: `${metrics.marginTop}px`,
		});
	};

	React.useEffect(() => {
		if (!isZoomEnabled) {
			setPinchScale(1);
			pinchScaleRef.current = 1;
			pinchStateRef.current.isPinching = false;
			touchPanStateRef.current.isPanning = false;
		}
	}, [isZoomEnabled, driveItem && driveItem.id]);

	React.useEffect(() => {
		pinchScaleRef.current = pinchScale;
	}, [pinchScale]);

	const centerZoomViewport = () => {
		const container = containerRef.current;
		if (!container) {
			return;
		}

		const centerX = Math.max((container.scrollWidth - container.clientWidth) / 2, 0);
		const centerY = Math.max((container.scrollHeight - container.clientHeight) / 2, 0);
		container.scrollLeft = centerX;
		container.scrollTop = centerY;
	};

	React.useEffect(() => {
		if (!driveItem || driveItem.type !== Api.DriveItemType.Image) {
			setZoomImageStyle(undefined);
			return;
		}

		updateZoomImageStyle();

		if (!isZoomEnabled) {
			const container = containerRef.current;
			if (container) {
				container.scrollLeft = 0;
				container.scrollTop = 0;
			}
			return;
		}

		// Wait one frame so style/layout updates are applied before centering.
		window.requestAnimationFrame(centerZoomViewport);
	}, [isZoomEnabled, driveItem && driveItem.id]);

	React.useEffect(() => {
		if (!isZoomEnabled || !driveItem || driveItem.type !== Api.DriveItemType.Image) {
			return;
		}

		// Keep rendered dimensions synced with pinch scale.
		updateZoomImageStyle();
	}, [pinchScale, isZoomEnabled, driveItem && driveItem.id]);

	React.useEffect(() => {
		const handleResize = () => updateZoomImageStyle();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [isZoomEnabled, driveItem && driveItem.id]);

	React.useLayoutEffect(() => {
		if (!isDragging) {
			return undefined;
		}

		const handleMouseMove = (event: MouseEvent) => {
			const container = containerRef.current;
			const dragState = dragStateRef.current;

			if (!container || !dragState.isDragging) {
				return;
			}

			const diffX = event.clientX - dragState.startX;
			const diffY = event.clientY - dragState.startY;
			container.scrollLeft = dragState.startScrollLeft - diffX;
			container.scrollTop = dragState.startScrollTop - diffY;
		};

		const handleMouseUp = () => {
			dragStateRef.current.isDragging = false;
			setIsDragging(false);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isDragging]);

	React.useEffect(() => {
		return () => {
			if (pinchScrollRafRef.current !== null) {
				window.cancelAnimationFrame(pinchScrollRafRef.current);
			}
		};
	}, []);

	if (driveItem) {
		let fileUrl;

		if (isOnline || isDownloaded) {
			if (isDownloaded) {
				fileUrl = `${Utils.getPersistentDownloadsFolder()}/${DriveItemUtils.getDownloadedFileName(driveItem)}`;
			} else {
				fileUrl = UrlUtils.getItemOriginalFileUrl(galleryConfig, driveItem.originalFile);
			}

			switch (driveItem.type) {
				case Api.DriveItemType.Movie:
					element = <VideoPlayer url={fileUrl} />;
					break;
				case Api.DriveItemType.Image:
					const imgSrc = DriveItemUtils.getTheaterImage(driveItem, galleryInfos.config, resolutions);
					const imageSrc = isDownloaded || isZoomEnabled ? fileUrl : imgSrc;
					const imageStyle = isZoomEnabled ? zoomImageStyle : undefined;
					element = (
						<Image
							key={`${driveItem.id}-${isZoomEnabled ? 'zoom' : 'default'}`}
							ref={imageRef}
							src={imageSrc}
							isZoomEnabled={isZoomEnabled}
							style={imageStyle}
							draggable={false}
							onLoad={() => {
								updateZoomImageStyle();
								if (isZoomEnabled) {
									window.requestAnimationFrame(centerZoomViewport);
								}
							}}
							onDragStart={event => event.preventDefault()}
						/>
					);
					break;
				case Api.DriveItemType.Sound:
					element = <AudioPlayer url={fileUrl} />;
					break;
				default:
					if (DriveItemUtils.hasIcon(driveItem)) {
						const thumbSrc = DriveItemUtils.getThumbnail(driveItem, galleryInfos.config, resolutions, true);
						element = <Image src={thumbSrc} />;
					} else {
						const svgIcon = DriveItemUtils.getDefaultThumbnail(driveItem);
						element = <SvgIcon><use xlinkHref={`#svgicon-${svgIcon}`} /></SvgIcon>;
					}
					break;
			}
		} else {
			if (DriveItemUtils.hasIcon(driveItem)) {
				fileUrl = Utils.getDeviceThumbnailPath(galleryInfos.config, thumbMap, driveItem, resolutions, false);
				element = <Image src={fileUrl} />;
			} else {
				const svgIcon = DriveItemUtils.getDefaultThumbnail(driveItem);
				element = <SvgIcon><use xlinkHref={`#svgicon-${svgIcon}`} /></SvgIcon>;
			}
		}
	}

	return (
		<StyledTheaterContent
			className="StyledTheaterContent"
			ref={containerRef}
			isZoomEnabled={isZoomEnabled}
			isDragging={isDragging}
			onMouseDown={handleMouseDown}
			onMouseLeave={stopDragging}
			onTouchStart={handleTouchStart}
			onTouchMove={handleTouchMove}
			onTouchEnd={handleTouchEnd}
		>
			{element}
		</StyledTheaterContent>
	);
};

const mapStateToProps = (state: StoreState) => {
	const { downloadManager, offlineManager } = state;
	return { downloadManager, offlineManager };
};

export default connect(mapStateToProps)(TheaterContent);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Theater/Content/index.tsx